// src/lib/toast.tsx

import * as React from "react";
import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast";
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "@ui/toast";
import { CheckCircle, AlertCircle, Info } from "lucide-react";

/** Maximum simultaneous toasts on screen */
const TOAST_LIMIT = 3;
/** How long before a toast auto‐closes (ms) */
const TOAST_REMOVE_DELAY = 3000;

/** ---------- 1. Define types & state/reducer logic ---------- **/

type ToasterToast = ToastProps & {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
};

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

type ActionType = typeof actionTypes;
type Action =
  | { type: ActionType["ADD_TOAST"]; toast: ToasterToast }
  | { type: ActionType["UPDATE_TOAST"]; toast: Partial<ToasterToast> }
  | { type: ActionType["DISMISS_TOAST"]; toastId?: string }
  | { type: ActionType["REMOVE_TOAST"]; toastId?: string };

interface State {
  toasts: ToasterToast[];
}

let memoryState: State = { toasts: [] };
const listeners: Array<(state: State) => void> = [];

let count = 0;
function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      return {
        ...state,
        toasts: [
          action.toast,
          ...state.toasts.filter((t) => t.id !== action.toast.id),
        ].slice(0, TOAST_LIMIT),
      };

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      };

    case "DISMISS_TOAST": {
      const { toastId } = action;
      if (toastId === undefined) {
        // dismiss all
        return {
          ...state,
          toasts: state.toasts.map((t) => ({ ...t, open: false })),
        };
      }
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId ? { ...t, open: false } : t
        ),
      };
    }

    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return { ...state, toasts: [] };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      };
  }
};

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => listener(memoryState));
}

/** ---------- 2. The global `toast()` function ---------- **/

type ToastFunctionProps = Omit<ToasterToast, "id">;

/**
 * Call this to show a new toast. Returns an object with:
 * - id: string (the ID of the new toast)
 * - dismiss(): () => void  (to close it early)
 * - update(newProps): () => void  (to change its title/description/variant, etc.)
 */
function toast({ ...props }: ToastFunctionProps) {
  const id = genId();
  const update = (newProps: ToasterToast) =>
    dispatch({ type: "UPDATE_TOAST", toast: { ...newProps, id } });
  const dismissToast = () =>
    dispatch({ type: "DISMISS_TOAST", toastId: id });

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      duration: TOAST_REMOVE_DELAY,
      onOpenChange: (open) => {
        if (!open) dismissToast();
      },
    },
  });

  return { id, dismiss: dismissToast, update };
}

/** ---------- 3. The `useToast()` hook ---------- **/

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const idx = listeners.indexOf(setState);
      if (idx > -1) listeners.splice(idx, 1);
    };
  }, []);

  return {
    toasts: state.toasts,
    toast,
    dismiss: (toastId?: string) =>
      dispatch({ type: "DISMISS_TOAST", toastId }),
  };
}

/** ---------- 4. The `<Toaster />` component that actually renders them ---------- **/

export function Toaster() {
  const { toasts, dismiss } = useToast();

  return (
    <ToastProvider>
      {toasts.map(({ id, title, description, action, variant, ...props }) => {
        let IconComponent;
        if (variant === "destructive") {
          IconComponent = AlertCircle;
        } else if (
          title?.toString().toLowerCase().includes("error")
        ) {
          IconComponent = AlertCircle;
        } else if (
          title?.toString().toLowerCase().includes("success")
        ) {
          IconComponent = CheckCircle;
        } else {
          IconComponent = Info;
        }

        return (
          <Toast
            key={id}
            {...props}
            variant={variant}
            className="group"
            onClick={() => dismiss(id)}
          >
            <div className="flex">
              {IconComponent && (
                <div className="mr-3 pt-0.5">
                  <IconComponent
                    className={`h-5 w-5 ${
                      variant === "destructive"
                        ? "text-destructive-foreground"
                        : "text-brand"
                    }`}
                  />
                </div>
              )}
              <div className="grid gap-1">
                {title && <ToastTitle>{title}</ToastTitle>}
                {description && (
                  <ToastDescription className="text-xs opacity-90">
                    {description}
                  </ToastDescription>
                )}
              </div>
            </div>
            {action}
            <ToastClose />
          </Toast>
        );
      })}
      <ToastViewport className="p-4 md:p-6" />
    </ToastProvider>
  );
}

// 5. Exports
export { useToast, toast };
